import { useEffect, useState } from "react";
import { Wallet as WalletIcon, ArrowDownCircle, ArrowUpCircle, Gift, RefreshCw, IndianRupee, Loader2 } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import MobileScreenHeader from "../components/MobileScreenHeader";

export default function Wallet() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [amount, setAmount] = useState("");
    const [upi, setUpi] = useState("");
    const [withdrawing, setWithdrawing] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/wallet/me");
            setData(data);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const withdraw = async () => {
        const n = parseFloat(amount);
        if (!n || n < 100) return toast.error("Minimum ₹100 withdrawal");
        if (!upi || upi.trim().length < 3) return toast.error("Enter your UPI ID or bank details");
        setWithdrawing(true);
        try {
            await api.post("/wallet/withdraw", { amount: n, upi_or_bank: upi.trim() });
            toast.success("Withdrawal requested");
            setAmount("");
            setUpi("");
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setWithdrawing(false);
        }
    };

    const w = data?.wallet;
    const txns = data?.transactions || [];
    const referrals = data?.referrals || [];
    const pending = data?.pending_withdrawals || [];

    return (
        <div className="min-h-screen bg-[#f6f7fb]" data-testid="wallet-page">
            <MobileScreenHeader title="Wallet" subtitle="Balance & transactions" testid="wallet-header" />

            {loading ? (
                <div className="flex items-center justify-center py-12 text-[#002657]/60">
                    <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
                </div>
            ) : (
                <>
                    {/* Balance card */}
                    <div className="px-4 -mt-2">
                        <div
                            className="text-on-image rounded-2xl p-5 shadow-xl"
                            style={{ background: "linear-gradient(135deg, #001a3d 0%, #002657 60%, #003366 100%)", boxShadow: "0 15px 35px -12px rgba(0,15,35,0.55)" }}
                            data-testid="wallet-balance-card"
                        >
                            <div className="flex items-center justify-between">
                                <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: "#F5E5A5" }}>Available Balance</div>
                                <WalletIcon className="w-5 h-5" style={{ color: "#F5E5A5" }} />
                            </div>
                            <div className="mt-2 font-heading font-extrabold text-white text-3xl flex items-baseline">
                                <IndianRupee className="w-6 h-6 mr-0.5" />
                                <span data-testid="wallet-balance">{(w?.balance || 0).toLocaleString("en-IN")}</span>
                            </div>
                            <div className="mt-1 text-[11px] text-white/70">Referral code: <span className="font-bold text-white">{data?.referral_code || "—"}</span></div>

                            <div className="mt-4 space-y-2">
                                <input
                                    type="text"
                                    value={upi}
                                    onChange={(e) => setUpi(e.target.value)}
                                    placeholder="UPI ID or bank a/c"
                                    data-testid="wallet-upi-input"
                                    className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/25 text-white placeholder:text-white/50 text-sm focus:outline-none focus:border-[#F5E5A5]"
                                />
                                <div className="flex items-center gap-2">
                                    <input
                                        type="number"
                                        inputMode="numeric"
                                        placeholder="Amount ≥ ₹100"
                                        value={amount}
                                        onChange={(e) => setAmount(e.target.value)}
                                        data-testid="wallet-amount-input"
                                        className="flex-1 px-3 py-2 rounded-lg bg-white/10 border border-white/25 text-white placeholder:text-white/50 text-sm focus:outline-none focus:border-[#F5E5A5]"
                                    />
                                    <button
                                        type="button"
                                        onClick={withdraw}
                                        disabled={withdrawing}
                                        data-testid="wallet-withdraw-btn"
                                        className="px-4 py-2 rounded-lg text-[13px] font-extrabold shadow disabled:opacity-60"
                                        style={{ background: "linear-gradient(90deg, #DAA520, #F59E0B)", color: "#001a3d" }}
                                    >
                                        {withdrawing ? "…" : "Withdraw"}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Pending withdrawals */}
                    {pending.length > 0 && (
                        <div className="mt-5 px-4">
                            <div className="text-[11px] uppercase tracking-widest font-extrabold text-[#8B6712] mb-2">Pending Withdrawals</div>
                            <div className="space-y-2">
                                {pending.map((p) => (
                                    <div key={p.id} className="rounded-2xl bg-white border border-[#DAA520]/30 p-3 flex items-center justify-between" data-testid={`wallet-pending-${p.id}`}>
                                        <div>
                                            <div className="font-bold text-[13px] text-[#002657]">₹{p.amount}</div>
                                            <div className="text-[10px] text-[#002657]/60">Pending admin review</div>
                                        </div>
                                        <RefreshCw className="w-4 h-4 text-amber-600" />
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Transactions */}
                    <div className="mt-5 px-4">
                        <div className="text-[11px] uppercase tracking-widest font-extrabold text-[#8B6712] mb-2">Transactions</div>
                        {txns.length === 0 ? (
                            <div className="rounded-2xl bg-white border border-[#002657]/10 p-6 text-center text-[#002657]/60 text-sm" data-testid="wallet-txns-empty">
                                No transactions yet.
                            </div>
                        ) : (
                            <div className="space-y-2" data-testid="wallet-txns-list">
                                {txns.map((t, i) => {
                                    const credit = (t.amount || 0) >= 0;
                                    return (
                                        <div key={t.id || i} className="rounded-2xl bg-white border border-[#002657]/10 p-3 flex items-center gap-3">
                                            <div className={`w-9 h-9 rounded-full flex items-center justify-center ${credit ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-600"}`}>
                                                {credit ? <ArrowDownCircle className="w-4 h-4" /> : <ArrowUpCircle className="w-4 h-4" />}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="text-[13px] font-bold text-[#002657] truncate">{t.reason || t.type || "Transaction"}</div>
                                                <div className="text-[10px] text-[#002657]/55">{(t.created_at || "").slice(0, 10)}</div>
                                            </div>
                                            <div className={`text-[13px] font-extrabold ${credit ? "text-emerald-600" : "text-red-600"}`}>
                                                {credit ? "+" : ""}₹{Math.abs(t.amount || 0).toLocaleString("en-IN")}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* Referrals summary */}
                    {referrals.length > 0 && (
                        <div className="mt-5 px-4 pb-6">
                            <div className="text-[11px] uppercase tracking-widest font-extrabold text-[#8B6712] mb-2 flex items-center gap-1">
                                <Gift className="w-3.5 h-3.5" /> Referrals ({referrals.length})
                            </div>
                            <div className="space-y-2" data-testid="wallet-referrals">
                                {referrals.slice(0, 10).map((r, i) => (
                                    <div key={r.id || i} className="rounded-2xl bg-white border border-[#002657]/10 p-3 flex items-center justify-between">
                                        <div className="min-w-0">
                                            <div className="text-[13px] font-bold text-[#002657] truncate">{r.referred_name || r.referred_email || "Friend"}</div>
                                            <div className="text-[10px] text-[#002657]/55">{r.status || "signed_up"}</div>
                                        </div>
                                        <div className="text-[12px] font-extrabold text-emerald-600">+₹{r.reward_amount || 0}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
